// Mission order on a field place page (card #4098), for the offline shell.
//
// The online page (Field::PlacesController#show) orders the same way in SQL.
// The two are held in step by test/fixtures/files/field_mission_order_cases.json,
// which both test/system/field/field_places_test.rb and
// test/pwa/field_place_mission_order_local.spec.js check against.
//
// Order:
//   1. Status group: In Progress, Scheduled, To Do (then anything else).
//   2. The chosen field (priority, name or scheduled date) in the chosen
//      direction. Blanks always sort last, whichever the direction.
//   3. Name sort key, ascending.
//   4. Full lowercase name, ascending.
//   5. Response id, ascending.
//
// Names compare by plain character (UTF-16 code unit) order, never
// localeCompare, so the browser's locale rules can't disagree with the
// server's COLLATE "C". "Week 10" sorts before "Week 2", same as online.

export const MISSION_SORT_STORAGE_KEY = "field_place_missions_sort"

export const MISSION_SORT_OPTIONS = ["priority", "name", "schedule_at"]

export const DEFAULT_MISSION_SORT = Object.freeze({ sortBy: "priority", sortDir: "asc" })

const STATUS_RANK = { started: 0, scheduled: 1, todo: 2, ended: 4 }
const UNKNOWN_STATUS_RANK = 3

// A leading mission number such as "1. ", "12) ", "3: " or "4 - " (card #4034).
// Mirrored in SQL as regexp_replace(missions.name, '^\s*\d+\s*[.):-]\s*', '').
const LEADING_NUMBER = /^\s*\d+\s*[.):-]\s*/

// The key a mission name sorts by: the leading number removed, lowercased.
// Falls back to the whole name if removing the number leaves nothing. The
// displayed name is never changed.
export function missionNameSortKey(name) {
  const full = String(name ?? "")
  const stripped = full.replace(LEADING_NUMBER, "")
  return (stripped || full).toLowerCase()
}

// A saved choice made safe to use: unknown fields and directions fall back
// to the default, so a stale or hand-edited value can't break the page.
export function normaliseMissionSort(saved) {
  const sortBy  = MISSION_SORT_OPTIONS.includes(saved?.sortBy) ? saved.sortBy : DEFAULT_MISSION_SORT.sortBy
  const sortDir = saved?.sortDir === "desc" ? "desc" : "asc"
  return { sortBy, sortDir }
}

// The Tasker's saved choice from localStorage, or the default when there is
// none or storage can't be read (private window).
export function readMissionSort(storage = globalThis.localStorage) {
  try {
    return normaliseMissionSort(JSON.parse(storage.getItem(MISSION_SORT_STORAGE_KEY)))
  } catch {
    return { ...DEFAULT_MISSION_SORT }
  }
}

// Sorts cached_responses rows (field_sync_shared.js#upsertCachedResponse
// shape) into place page order. Returns a new array.
export function sortCachedResponses(records, sort = DEFAULT_MISSION_SORT) {
  const { sortBy, sortDir } = normaliseMissionSort(sort)
  return records.slice().sort((a, b) => compareCachedResponses(a, b, sortBy, sortDir))
}

function compareCachedResponses(a, b, sortBy, sortDir) {
  return compareValues(statusRank(a), statusRank(b)) ||
         compareChosenField(a, b, sortBy, sortDir) ||
         compareValues(missionNameSortKey(a.missionName), missionNameSortKey(b.missionName)) ||
         compareValues(fullLowerName(a), fullLowerName(b)) ||
         compareValues(Number(a.responseId), Number(b.responseId))
}

function compareChosenField(a, b, sortBy, sortDir) {
  if (sortBy === "name") {
    const order = compareValues(missionNameSortKey(a.missionName), missionNameSortKey(b.missionName))
    return sortDir === "desc" ? -order : order
  }

  const valueOf = sortBy === "schedule_at" ? scheduledTime : priorityOf
  return compareBlanksLast(valueOf(a), valueOf(b), sortDir)
}

// Blanks (null) always last; only the non-blank comparison flips for desc.
function compareBlanksLast(a, b, sortDir) {
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  const order = compareValues(a, b)
  return sortDir === "desc" ? -order : order
}

function statusRank(record) {
  return STATUS_RANK[record.status] ?? UNKNOWN_STATUS_RANK
}

function priorityOf(record) {
  const value = record.missionPriority
  if (value === null || value === undefined || value === "") return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function scheduledTime(record) {
  if (!record.scheduledAt) return null
  const time = Date.parse(record.scheduledAt)
  return Number.isFinite(time) ? time : null
}

function fullLowerName(record) {
  return String(record.missionName ?? "").toLowerCase()
}

// Plain < and > -- numbers numerically, strings by code unit.
function compareValues(a, b) {
  return a < b ? -1 : a > b ? 1 : 0
}
